import { RoomEvent, Track, type Room } from 'livekit-client';
import { client } from './client';
import { denoiseContext } from './denoise';

/**
 * The last two minutes of a call, kept in memory only, for "Teşhis kaydını gönder": the level,
 * loudest frequency and tonality of every incoming microphone (muted ones too), with the call's
 * events and the app's own cues. Sent only when the user presses the button. Built to catch a
 * steady tone that both sides heard (docs/plans/2026-11-02-mobile-plan.md).
 */
const WINDOW_MS = 120_000;
const EVERY_MS = 100;

interface Sample {
  t: number;
  who: string;
  muted: boolean;
  /** RMS of the waveform, 0–1. */
  rms: number;
  /** The loudest frequency between 100 Hz and 8 kHz. */
  peakHz: number;
  /** How far the loudest bin stands above the average of the band, in dB: a pure tone is far above speech. */
  tonalDb: number;
}

let samples: Sample[] = [];
let events: { t: number; event: string; detail?: string }[] = [];
let startedAt = 0;

const now = () => Math.round(performance.now() - startedAt);

function trim() {
  const from = now() - WINDOW_MS;
  if (samples.length > 0 && samples[0]!.t < from) samples = samples.filter((s) => s.t >= from);
  if (events.length > 0 && events[0]!.t < from) events = events.filter((e) => e.t >= from);
}

export function noteCallEvent(event: string, detail?: string) {
  if (!startedAt) return;
  events.push({ t: now(), event, ...(detail ? { detail } : {}) });
}

/** Starts recording a call; returns the stop. */
export function startCallDiagnostics(room: Room): () => void {
  samples = [];
  events = [];
  startedAt = performance.now();
  const ctx = denoiseContext();
  const analysers = new Map<MediaStreamTrack, AnalyserNode>();
  const time = new Float32Array(2048);
  const spectrum = new Float32Array(1024);

  const recorded = Object.values(RoomEvent).filter((e) => e !== RoomEvent.ActiveSpeakersChanged && e !== RoomEvent.DataReceived);
  const listeners = recorded.map((event) => {
    const listener = (...args: unknown[]) => {
      const first = args[0] as { identity?: string; source?: string; kind?: string } | string | undefined;
      const detail = typeof first === 'string' ? first : (first?.identity ?? first?.source ?? first?.kind);
      noteCallEvent(`room:${event}`, detail);
    };
    room.on(event, listener);
    return [event, listener] as const;
  });
  const onDevices = () => noteCallEvent('devicechange');
  navigator.mediaDevices?.addEventListener('devicechange', onDevices);
  let contextState = ctx.state;

  const timer = setInterval(() => {
    if (ctx.state !== contextState) noteCallEvent('audiocontext', (contextState = ctx.state));
    const t = now();
    const live = new Set<MediaStreamTrack>();
    for (const participant of room.remoteParticipants.values()) {
      const publication = participant.getTrackPublication(Track.Source.Microphone);
      const track = publication?.track?.mediaStreamTrack;
      if (!track || track.readyState !== 'live') continue;
      live.add(track);
      let analyser = analysers.get(track);
      if (!analyser) {
        analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        ctx.createMediaStreamSource(new MediaStream([track])).connect(analyser);
        analysers.set(track, analyser);
      }
      analyser.getFloatTimeDomainData(time);
      let sum = 0;
      for (const v of time) sum += v * v;
      analyser.getFloatFrequencyData(spectrum);
      const binHz = ctx.sampleRate / analyser.fftSize;
      const from = Math.ceil(100 / binHz);
      const to = Math.min(spectrum.length - 1, Math.floor(8000 / binHz));
      let peak = from;
      let total = 0;
      for (let k = from; k <= to; k++) {
        total += spectrum[k]!;
        if (spectrum[k]! > spectrum[peak]!) peak = k;
      }
      const tonal = spectrum[peak]! - total / (to - from + 1);
      samples.push({
        t,
        who: participant.identity,
        muted: publication?.isMuted ?? false,
        rms: Math.round(Math.sqrt(sum / time.length) * 10_000) / 10_000,
        peakHz: Math.round(peak * binHz),
        tonalDb: Number.isFinite(tonal) ? Math.round(tonal) : 0,
      });
    }
    for (const [track, analyser] of analysers) {
      if (!live.has(track)) {
        analyser.disconnect();
        analysers.delete(track);
      }
    }
    trim();
  }, EVERY_MS);

  return () => {
    clearInterval(timer);
    for (const [event, listener] of listeners) room.off(event, listener);
    navigator.mediaDevices?.removeEventListener('devicechange', onDevices);
    for (const analyser of analysers.values()) analyser.disconnect();
  };
}

/** Sends the last two minutes; true when the server took it. */
export async function sendCallDiagnostics(note: string): Promise<boolean> {
  if (!startedAt) return false;
  trim();
  const body = { note, userAgent: navigator.userAgent, sentAt: new Date().toISOString(), now: now(), events, samples };
  return client.api
    .sendDiagnostics(body)
    .then(() => true)
    .catch(() => false);
}
